import { useCompanyList } from "@/api/services/admin/company";
import { Tab } from "@/components/elements/Tab";
import { TopPage } from "@/components/elements/TopPage";
import { DEFAULT_PER_PAGE, START_PAGE } from "@/constants/pagination";
import { CompanyListPagination } from "@/features/company/components/CompanyListPagination";
import { CompanyListTop } from "@/features/company/components/CompanyListTop";
import { CompanyTable } from "@/features/company/components/CompanyTable";
import { COMPANY_LIST_TAB, COMPANY_LIST_TAB_LABEL } from "@/features/company/constants/companyListTab";
import { useGetCompanyTotalCount } from "@/features/company/hooks/useCompanyTotalCount";
import { useCompanyFilterCache } from "@/features/company/provider/CompanyListFilterCacheProvider";
import { background, divider } from "@/theme/colors";
import { ECompanyContractStatus } from "@/types/enum";
import { Box, Stack } from "@mui/material";
import { FC, useMemo } from "react";
import { COMPANY_COMMON_SORT_FIELDS, COMPANY_TAB_DATE_SORT_FIELD } from "@/features/company/components/CompanyTable";

type TCompanyListPageProps = {};

export const CompanyListPage: FC<TCompanyListPageProps> = () => {
  const { companyFilterCache, setCompanyFilterCache } = useCompanyFilterCache();

  const companyCounts = useGetCompanyTotalCount(companyFilterCache?.keyword || undefined);

  const searchTab = companyFilterCache?.tab || COMPANY_LIST_TAB.DURING_CONTRACT;

  const contractStatuses = useMemo(() => {
    if (searchTab === COMPANY_LIST_TAB.DURING_CONTRACT) return [ECompanyContractStatus.DURING_CONTRACT];
    else if (searchTab === COMPANY_LIST_TAB.EXPIRED_CONTRACT) return [ECompanyContractStatus.EXPIRED_CONTRACT];
    else if (searchTab === COMPANY_LIST_TAB.TRIAL) return [ECompanyContractStatus.TRIAL];
    else if (searchTab === COMPANY_LIST_TAB.TRIAL_CANCELLED) return [ECompanyContractStatus.TRIAL_CANCELLED];
    else return [ECompanyContractStatus.DURING_CONTRACT];
  }, [searchTab]);

  // Sort is kept in cache across tabs, but only applied to the API when the sorted
  // column actually exists on the current tab. The cache stays untouched so returning
  // to the original tab restores its sort (unless the user picked a new sort meanwhile).
  const activeSorts = useMemo(() => {
    const validFields = [...COMPANY_COMMON_SORT_FIELDS, COMPANY_TAB_DATE_SORT_FIELD[searchTab]];
    return companyFilterCache?.sorts?.filter((sort) => validFields.includes(sort.split("|")[0]));
  }, [companyFilterCache?.sorts, searchTab]);

  const { data: companyListResult, isLoading } = useCompanyList({
    page: companyFilterCache?.page || START_PAGE,
    contractStatuses,
    perPage: DEFAULT_PER_PAGE,
    sorts: activeSorts,
    keyword: companyFilterCache?.keyword || undefined,
  });

  return (
    <Stack gap={2.5}>
      <TopPage title="受け入れ企業の一覧" enablePageBack={false} />
      <Stack
        sx={{
          border: `1px solid ${divider.middle}`,
          borderTop: "none",
          backgroundColor: background.white,
          borderRadius: "8px",
        }}
      >
        <Tab
          value={searchTab}
          onChange={(_, tab) => {
            setCompanyFilterCache({ keyword: companyFilterCache?.keyword, sorts: companyFilterCache?.sorts, tab: tab.toString(), page: START_PAGE });
          }}
          options={[
            {
              label: `${COMPANY_LIST_TAB_LABEL[COMPANY_LIST_TAB.DURING_CONTRACT]} (${companyCounts?.duringContractCount}) `,
              value: COMPANY_LIST_TAB.DURING_CONTRACT,
            },
            {
              label: `${COMPANY_LIST_TAB_LABEL[COMPANY_LIST_TAB.EXPIRED_CONTRACT]} (${companyCounts?.expiredContractCount}) `,
              value: COMPANY_LIST_TAB.EXPIRED_CONTRACT,
            },
            {
              label: `${COMPANY_LIST_TAB_LABEL[COMPANY_LIST_TAB.TRIAL]} (${companyCounts?.trialCount}) `,
              value: COMPANY_LIST_TAB.TRIAL,
            },
            {
              label: `${COMPANY_LIST_TAB_LABEL[COMPANY_LIST_TAB.TRIAL_CANCELLED]} (${companyCounts?.trialCancelledCount}) `,
              value: COMPANY_LIST_TAB.TRIAL_CANCELLED,
            },
          ]}
        />
        <Box padding="24px">
          <Stack gap={3}>
            <CompanyListTop pagination={companyListResult?.pagination} />
            <CompanyTable contractStatuses={contractStatuses} data={companyListResult?.data ?? []} isLoading={isLoading} />
            <CompanyListPagination totalPage={companyListResult?.pagination?.totalPage ?? 0} />
          </Stack>
        </Box>
      </Stack>
    </Stack>
  );
};
